import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';

import { cn } from '@/lib/utils';
import { useKeyboardInset } from '@/utils/keyboardInset';

const Drawer = ({
  shouldScaleBackground = true,
  repositionInputs = false,
  ...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
  <DrawerPrimitive.Root
    shouldScaleBackground={shouldScaleBackground}
    repositionInputs={repositionInputs}
    {...props}
  />
);
Drawer.displayName = 'Drawer';

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn('fixed inset-0 z-50 bg-black/80', className)}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> & {
    overlayClassName?: string;
    /** Оформление «палочки» сверху — нужен там, где отступ по умолчанию лишний. */
    handleClassName?: string;
    /**
     * Поднимать шторку над экранной клавиатурой. Нужно шторкам с полем ввода
     * в Telegram на iOS, где клавиатура перекрывает страницу, а не ужимает её
     * (см. useKeyboardInset). Vaul'овский repositionInputs выключен: он ещё и
     * меняет высоту шторки, и делает это рывками.
     */
    liftAboveKeyboard?: boolean;
  }
>(
  (
    {
      className,
      overlayClassName,
      handleClassName,
      liftAboveKeyboard = false,
      children,
      ...props
    },
    ref
  ) => {
    const keyboardInset = useKeyboardInset(liftAboveKeyboard);

    return (
      <DrawerPortal>
        <DrawerOverlay className={overlayClassName} />
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            'fixed inset-x-0 bottom-0 z-50 mt-24 flex h-auto flex-col rounded-t-[24px] border bg-fill-background',
            className
          )}
          {...props}
        >
          <div
            className={cn(
              'mx-auto mt-2 h-1.5 w-[80px] rounded-full bg-gray-200',
              handleClassName
            )}
          />
          <div className="flex-1 overflow-y-auto" style={{ maxHeight: '85dvh' }}>
            {children}
          </div>
          {/* Распорка под клавиатуру — отдельным элементом, а не отступом на
              самой шторке: её inline-стили (transform, transition) переписывает
              vaul, и они бы конфликтовали. Живёт вне скролл-обёртки, чтобы не
              отъедать у контента место из maxHeight. */}
          {liftAboveKeyboard && (
            <div
              aria-hidden
              className="shrink-0 transition-[height] duration-300 ease-out"
              style={{ height: keyboardInset }}
            />
          )}
        </DrawerPrimitive.Content>
      </DrawerPortal>
    );
  }
);
DrawerContent.displayName = 'DrawerContent';

const DrawerHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('grid gap-1.5 p-4 text-center sm:text-left', className)}
    {...props}
  />
);
DrawerHeader.displayName = 'DrawerHeader';

const DrawerFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mt-auto flex flex-col gap-2 p-4', className)}
    {...props}
  />
);
DrawerFooter.displayName = 'DrawerFooter';

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-none tracking-tight',
      className
    )}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
